import React, { useState, useEffect, useCallback } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import Layout from '../components/Layout';
import { accountingAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const fmtNum = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtDate = d => formatDateDMY(d, '—');

const Accounting = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#E2E8F0' : '#1E293B' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#FFFFFF', borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, padding: 20 };
  const inp = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#E2E8F0' : '#1E293B', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const btnPrimary = { padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnSecondary = { padding: '8px 18px', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
  const lbl = { ...muted, fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' };

  const [tab, setTab] = useState('coa');
  const [accounts, setAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [trialBalance, setTrialBalance] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [bankTxns, setBankTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showJEForm, setShowJEForm] = useState(false);
  const [showAccForm, setShowAccForm] = useState(false);

  // JE Form
  const [jeForm, setJeForm] = useState({ date: new Date().toISOString().slice(0, 10), description: '', reference: '', lines: [{ account_id: '', debit: 0, credit: 0 }, { account_id: '', debit: 0, credit: 0 }] });

  // Account Form
  const [accForm, setAccForm] = useState({ code: '', name: '', type: 'asset', parent_id: '' });

  const loadAccounts = useCallback(async () => {
    try { const r = await accountingAPI.listAccounts(); setAccounts(Array.isArray(r.data) ? r.data : []); } catch (e) { console.error(e); }
  }, []);

  const loadTab = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'coa') await loadAccounts();
      else if (tab === 'journal') { const r = await accountingAPI.listJournalEntries(); setJournalEntries(Array.isArray(r.data?.items) ? r.data.items : []); }
      else if (tab === 'trial') { const r = await accountingAPI.trialBalance(); setTrialBalance(r.data); }
      else if (tab === 'balance') { const r = await accountingAPI.balanceSheet(); setBalanceSheet(r.data); }
      else if (tab === 'cashflow') { const r = await accountingAPI.cashFlow(); setCashFlow(r.data); }
      else if (tab === 'bank') { const r = await accountingAPI.listBankTransactions(); setBankTxns(Array.isArray(r.data?.items) ? r.data.items : []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tab, loadAccounts]);

  useEffect(() => { loadTab(); }, [loadTab]);

  const initAccounts = async () => {
    try { await accountingAPI.initializeAccounts(); loadAccounts(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const createAccount = async (e) => {
    e.preventDefault();
    try {
      await accountingAPI.createAccount({
        account_code: accForm.code,
        account_name: accForm.name,
        account_type: accForm.type,
        parent_id: accForm.parent_id || null,
      });
      setShowAccForm(false);
      setAccForm({ code: '', name: '', type: 'asset', parent_id: '' });
      loadAccounts();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const createJE = async (e) => {
    e.preventDefault();
    try {
      await accountingAPI.createJournalEntry({
        entry_date: jeForm.date,
        narration: jeForm.description,
        reference_type: jeForm.reference || null,
        lines: jeForm.lines,
      });
      setShowJEForm(false);
      setJeForm({ date: new Date().toISOString().slice(0, 10), description: '', reference: '', lines: [{ account_id: '', debit: 0, credit: 0 }, { account_id: '', debit: 0, credit: 0 }] });
      loadTab();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { key: 'coa', label: 'Chart of Accounts' },
    { key: 'journal', label: 'Journal Entries' },
    { key: 'trial', label: 'Trial Balance' },
    { key: 'balance', label: 'Balance Sheet' },
    { key: 'cashflow', label: 'Cash Flow' },
    { key: 'bank', label: 'Bank Reconciliation' },
  ];

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, marginBottom: 20 }}>📒 Accounting</h1>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${tab === t.key ? acc : (darkMode ? '#334155' : '#E2E8F0')}`, background: tab === t.key ? acc : 'transparent', color: tab === t.key ? '#fff' : pg.color, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div style={{ ...muted, textAlign: 'center', padding: 40 }}>Loading...</div>}

        {/* Chart of Accounts */}
        {tab === 'coa' && !loading && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={initAccounts} style={btnSecondary}>Initialize Default Accounts</button>
              <button onClick={() => setShowAccForm(!showAccForm)} style={btnPrimary}>{showAccForm ? 'Cancel' : '+ New Account'}</button>
            </div>

            {showAccForm && (
              <form onSubmit={createAccount} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  <div><label style={lbl}>Code</label><input value={accForm.code} onChange={e => setAccForm(f => ({ ...f, code: e.target.value }))} style={inp} required /></div>
                  <div><label style={lbl}>Name</label><input value={accForm.name} onChange={e => setAccForm(f => ({ ...f, name: e.target.value }))} style={inp} required /></div>
                  <div><label style={lbl}>Type</label>
                    <select value={accForm.type} onChange={e => setAccForm(f => ({ ...f, type: e.target.value }))} style={inp}>
                      {['asset', 'liability', 'equity', 'income', 'expense'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}><button type="submit" style={btnPrimary}>Create</button></div>
                </div>
              </form>
            )}

            {accounts.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No accounts yet. Click "Initialize Default Accounts" to set up Indian accounting chart.</div>
            ) : (
              <div style={card}>
                {['asset', 'liability', 'equity', 'income', 'expense'].map(type => {
                  const accs = accounts.filter(a => a.account_type === type);
                  if (accs.length === 0) return null;
                  return (
                    <div key={type} style={{ marginBottom: 20 }}>
                      <h3 style={{ ...pg, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8, padding: '6px 0', borderBottom: `2px solid ${acc}` }}>{type}s</h3>
                      {accs.map(a => (
                        <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                          <span style={{ ...pg, fontSize: 13 }}><span style={muted}>{a.account_code}</span> — {a.account_name}</span>
                          <span style={{ ...pg, fontSize: 13, fontWeight: 600 }}>₹{fmtNum(a.current_balance)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Journal Entries */}
        {tab === 'journal' && !loading && (
          <div>
            <button onClick={() => setShowJEForm(!showJEForm)} style={{ ...btnPrimary, marginBottom: 16 }}>{showJEForm ? 'Cancel' : '+ New Journal Entry'}</button>

            {showJEForm && (
              <form onSubmit={createJE} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div><label style={lbl}>Date</label><StrictDateInput value={jeForm.date} onChange={e => setJeForm(f => ({ ...f, date: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Description</label><input value={jeForm.description} onChange={e => setJeForm(f => ({ ...f, description: e.target.value }))} style={inp} required /></div>
                  <div><label style={lbl}>Reference</label><input value={jeForm.reference} onChange={e => setJeForm(f => ({ ...f, reference: e.target.value }))} style={inp} /></div>
                </div>
                <h4 style={{ ...pg, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Lines</h4>
                {jeForm.lines.map((line, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                    <div><label style={lbl}>Account</label>
                      <select value={line.account_id} onChange={e => { const ls = [...jeForm.lines]; ls[idx].account_id = e.target.value; setJeForm(f => ({ ...f, lines: ls })); }} style={inp}>
                        <option value="">Select Account</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                      </select>
                    </div>
                    <div><label style={lbl}>Debit</label><input type="number" value={line.debit} onChange={e => { const ls = [...jeForm.lines]; ls[idx].debit = Number(e.target.value); setJeForm(f => ({ ...f, lines: ls })); }} style={inp} /></div>
                    <div><label style={lbl}>Credit</label><input type="number" value={line.credit} onChange={e => { const ls = [...jeForm.lines]; ls[idx].credit = Number(e.target.value); setJeForm(f => ({ ...f, lines: ls })); }} style={inp} /></div>
                    {jeForm.lines.length > 2 && <button type="button" onClick={() => setJeForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))} style={{ ...btnSecondary, padding: '8px 10px', color: '#EF4444' }}>✕</button>}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setJeForm(f => ({ ...f, lines: [...f.lines, { account_id: '', debit: 0, credit: 0 }] }))} style={btnSecondary}>+ Add Line</button>
                  <button type="submit" style={btnPrimary}>Post Entry</button>
                </div>
              </form>
            )}

            {journalEntries.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No journal entries yet</div>
            ) : (
              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['Entry No', 'Date', 'Description', 'Reference', 'Total'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {journalEntries.map(je => (
                      <tr key={je.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>{je.entry_number}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{fmtDate(je.entry_date)}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{je.narration}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{je.reference_type || '—'}</td>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 700 }}>₹{fmtNum((je.lines || []).reduce((s, l) => s + Number(l.debit || 0), 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Trial Balance */}
        {tab === 'trial' && !loading && trialBalance && (
          <div style={card}>
            <h3 style={{ ...pg, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Trial Balance</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                  {['Code', 'Account', 'Type', 'Debit', 'Credit'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(trialBalance.items || []).map((a, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <td style={{ padding: '8px 10px', ...muted }}>{a.account_code}</td>
                    <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>{a.account_name}</td>
                    <td style={{ padding: '8px 10px', ...pg, textTransform: 'capitalize' }}>{a.account_type}</td>
                    <td style={{ padding: '8px 10px', ...pg }}>₹{fmtNum(a.debit)}</td>
                    <td style={{ padding: '8px 10px', ...pg }}>₹{fmtNum(a.credit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: `2px solid ${acc}`, fontWeight: 800 }}>
                  <td colSpan={3} style={{ padding: '10px', ...pg }}>Total</td>
                  <td style={{ padding: '10px', ...pg }}>₹{fmtNum(trialBalance.total_debit)}</td>
                  <td style={{ padding: '10px', ...pg }}>₹{fmtNum(trialBalance.total_credit)}</td>
                </tr>
              </tfoot>
            </table>
            {trialBalance.is_balanced ? (
              <div style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, background: '#DCFCE7', color: '#166534', fontWeight: 700, fontSize: 13 }}>✅ Trial Balance is balanced</div>
            ) : (
              <div style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, background: '#FEE2E2', color: '#991B1B', fontWeight: 700, fontSize: 13 }}>⚠️ Trial Balance is NOT balanced</div>
            )}
          </div>
        )}

        {/* Balance Sheet */}
        {tab === 'balance' && !loading && balanceSheet && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={card}>
                <h3 style={{ ...pg, fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#3B82F6' }}>Assets</h3>
                {(balanceSheet.assets?.items || []).map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <span style={{ ...pg, fontSize: 13 }}>{a.name}</span>
                    <span style={{ ...pg, fontSize: 13, fontWeight: 600 }}>₹{fmtNum(a.balance)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', marginTop: 8, borderTop: `2px solid #3B82F6`, fontWeight: 800, ...pg }}>
                  <span>Total Assets</span><span>₹{fmtNum(balanceSheet.assets?.total)}</span>
                </div>
              </div>
              <div style={card}>
                <h3 style={{ ...pg, fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#EF4444' }}>Liabilities & Equity</h3>
                {(balanceSheet.liabilities?.items || []).map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <span style={{ ...pg, fontSize: 13 }}>{a.name}</span>
                    <span style={{ ...pg, fontSize: 13, fontWeight: 600 }}>₹{fmtNum(a.balance)}</span>
                  </div>
                ))}
                {(balanceSheet.equity?.items || []).map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <span style={{ ...pg, fontSize: 13 }}>{a.name}</span>
                    <span style={{ ...pg, fontSize: 13, fontWeight: 600 }}>₹{fmtNum(a.balance)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', marginTop: 8, borderTop: `2px solid #EF4444`, fontWeight: 800, ...pg }}>
                  <span>Total Liabilities + Equity</span><span>₹{fmtNum((balanceSheet.liabilities?.total || 0) + (balanceSheet.equity?.total || 0))}</span>
                </div>
              </div>
            </div>
            {balanceSheet.simplified?.net_profit !== undefined && (
              <div style={{ ...card, marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...pg, fontWeight: 700 }}>Net Profit (P&L)</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: balanceSheet.simplified.net_profit >= 0 ? '#10B981' : '#EF4444' }}>₹{fmtNum(balanceSheet.simplified.net_profit)}</span>
              </div>
            )}
          </div>
        )}

        {/* Cash Flow */}
        {tab === 'cashflow' && !loading && cashFlow && (
          <div>
            <div className="stagger-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
              <div style={{ ...card, borderLeft: `4px solid #3B82F6` }}><div style={muted}>Cash Inflow</div><div style={{ fontSize: 22, fontWeight: 800, color: '#10B981' }}>₹{fmtNum(cashFlow.operating_activities?.total_cash_in)}</div></div>
              <div style={{ ...card, borderLeft: `4px solid #EF4444` }}><div style={muted}>Cash Outflow</div><div style={{ fontSize: 22, fontWeight: 800, color: '#EF4444' }}>₹{fmtNum(cashFlow.operating_activities?.total_cash_out)}</div></div>
              <div style={{ ...card, borderLeft: `4px solid ${acc}` }}><div style={muted}>Net Cash Flow</div><div style={{ fontSize: 22, fontWeight: 800, color: acc }}>₹{fmtNum(cashFlow.summary?.net_cash_flow)}</div></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={card}>
                <h3 style={{ ...pg, fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#10B981' }}>Inflow</h3>
                {cashFlow.operating_activities && Object.entries(cashFlow.operating_activities).filter(([k]) => ['cash_from_customers', 'other_receipts'].includes(k)).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <span style={{ ...pg, fontSize: 13, textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
                    <span style={{ ...pg, fontSize: 13, fontWeight: 600 }}>₹{fmtNum(v)}</span>
                  </div>
                ))}
              </div>
              <div style={card}>
                <h3 style={{ ...pg, fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#EF4444' }}>Outflow</h3>
                {cashFlow.operating_activities && Object.entries(cashFlow.operating_activities).filter(([k]) => ['paid_to_suppliers', 'operating_expenses', 'salaries_paid'].includes(k)).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <span style={{ ...pg, fontSize: 13, textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
                    <span style={{ ...pg, fontSize: 13, fontWeight: 600 }}>₹{fmtNum(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bank Reconciliation */}
        {tab === 'bank' && !loading && (
          <div style={card}>
            <h3 style={{ ...pg, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Bank Transactions</h3>
            {bankTxns.length === 0 ? (
              <div style={{ ...muted, textAlign: 'center', padding: 30 }}>No bank transactions imported yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                    {['Date', 'Description', 'Debit', 'Credit', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bankTxns.map(t => (
                    <tr key={t.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                      <td style={{ padding: '8px 10px', ...pg }}>{fmtDate(t.transaction_date)}</td>
                      <td style={{ padding: '8px 10px', ...pg }}>{t.description}</td>
                      <td style={{ padding: '8px 10px', color: '#EF4444' }}>{t.debit > 0 ? `₹${fmtNum(t.debit)}` : '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#10B981' }}>{t.credit > 0 ? `₹${fmtNum(t.credit)}` : '—'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: t.is_reconciled ? '#DCFCE7' : '#FEF3C7', color: t.is_reconciled ? '#166534' : '#92400E' }}>
                          {t.is_reconciled ? 'Reconciled' : 'Pending'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {!t.is_reconciled && <button onClick={async () => { await accountingAPI.reconcile(t.id); loadTab(); }} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11 }}>Reconcile</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Accounting;
